import React, { Fragment, useEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { Transition } from "@headlessui/react"
import { CheckCircleIcon, ExclamationCircleIcon, XCircleIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { ArrowPathIcon, InformationCircleIcon } from "@heroicons/react/24/solid"
import classNames from "../../utils/classNames"
import Button from "./Button"

interface ComponentProps {
  visible: boolean
  title: string
  description?: string
  success?: boolean
  warning?: boolean
  error?: boolean
  iconSuccess?: boolean
  iconWarning?: boolean
  iconError?: boolean
  refreshButton?: boolean
  onDismiss?: (...args: any[]) => any
  dismissAfter?: number
  showTimer?: boolean
  onClick?: (...any: any) => any
}

export const NotificationContainer = (props: { children: JSX.Element | JSX.Element[] }) => {
  return ReactDOM.createPortal(
    <div
      aria-live="assertive"
      className="fixed bottom-4 right-0 w-full flex flex-col-reverse items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start"
      style={{ zIndex: 999999, transformOrigin: "bottom" }}
    >
      <div className="w-full flex flex-col items-start space-y-4 sm:items-start">{props.children}</div>
    </div>,
    document.body,
  )
}

const Notification = (props: ComponentProps) => {
  const [show, setShow] = useState(props.visible)
  const [showTimeout, setShowTimeout] = useState(setTimeout(() => { }, 0))
  const [timeRemaining, setTimeRemaining] = useState(props.dismissAfter || 5000)
  const [calculatedBottom, setCalculatedBottom] = useState(0)
  const notificationRef = useRef<HTMLDivElement>(null)

  let iconComponent = <InformationCircleIcon className="h-8 w-8 text-gray-500" />

  if (props.success) iconComponent = <CheckCircleIcon className="h-8 w-8 text-green-400" />
  if (props.warning) iconComponent = <ExclamationCircleIcon className="h-8 w-8 text-yellow-400" />
  if (props.error) iconComponent = <XCircleIcon className="h-8 w-8 text-red-400" />

  if (props.iconSuccess) iconComponent = <CheckCircleIcon className="h-8 w-8" />
  if (props.iconWarning) iconComponent = <ExclamationCircleIcon className="h-8 w-8" />
  if (props.iconError) iconComponent = <XCircleIcon className="h-8 w-8" />

  const dismissMe = () => {
    setShow(false)
    if (props.onDismiss) props.onDismiss()
  }

  useEffect(() => {
    setShow(props.visible)
    setTimeRemaining(props.dismissAfter || 5000)

    if (props.visible && props.onDismiss && (props.dismissAfter || props.success)) {
      clearTimeout(showTimeout)

      let intervalId: NodeJS.Timeout | null = null

      if (props.showTimer) {
        intervalId = setInterval(() => {
          setTimeRemaining((prev) => {
            if (prev <= 100) {
              if (intervalId) clearInterval(intervalId)
              return 0
            }
            return prev - 100
          })
        }, 100)
      }

      const timeoutId = setTimeout(() => {
        dismissMe()
        if (intervalId) clearInterval(intervalId)
      }, props.dismissAfter || 5000)

      setShowTimeout(timeoutId)

      return () => {
        clearTimeout(timeoutId)
        if (intervalId) clearInterval(intervalId)
      }
    }
  }, [props.visible])

  useEffect(() => {
    if (notificationRef.current) {
      const rect = notificationRef.current.getBoundingClientRect()
      const offset = Math.max(20 - (window.innerHeight - rect.bottom), 0)
      setCalculatedBottom(offset)
    }
  }, [show])

  if (!props.visible) return null

  const timerPercentage = (((props.dismissAfter || 5000) - timeRemaining) / (props.dismissAfter || 5000)) * 100

  return (
    <NotificationContainer>
      <Transition
        show={show}
        as={Fragment}
        enter="transform ease-out duration-300 transition"
        enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enterTo="translate-y-0 opacity-100 sm:translate-x-0"
        leave="transition ease-in duration-100"
        leaveFrom="translate-y-0 opacity-100 sm:translate-x-0"
        leaveTo="translate-y-2 opacity-0 sm:translate-x-2"
      >
        <div
          ref={notificationRef}
          className={classNames(
            "max-w-lg w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden",
            props.onClick && "cursor-pointer hover:bg-gray-50",
          )}
          style={{
            zIndex: 999999,
            position: "absolute",
            bottom: calculatedBottom,
          }}
          onClick={(args) => {
            if (props.onClick) {
              props.onClick(args)
              dismissMe()
            }
          }}
        >
          <div className="p-6 relative">
            <div className="flex items-start">
              <div className="flex-shrink-0">{iconComponent}</div>
              <div className="ml-3 w-0 flex-1 pt-0.5">
                <p className="text-lg text-gray-800">{props.title}</p>
                {props.description && <p className="mt-1 text-gray-500">{props.description}</p>}
                {props.refreshButton && (
                  <Button
                    variant="white"
                    className="mt-4"
                    icon={<ArrowPathIcon className="h-5 w-5" aria-hidden="true" />}
                    onClick={() => {
                      location.reload()
                    }}
                  >
                    Reload
                  </Button>
                )}
              </div>
              {props.onDismiss && (
                <div className="ml-4 flex-shrink-0 flex cursor-pointer">
                  <button
                    data-testid="notification-close-button"
                    className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={(e) => {
                      e.preventDefault()
                      dismissMe()
                    }}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {props.showTimer && props.onDismiss && (props.dismissAfter || props.success) && (
            <div className="w-full h-0.5 bg-gray-200 relative">
              <div
                className={classNames(
                  "h-full transition-all duration-100 ease-linear absolute right-0",
                  props.success
                    ? "bg-green-400"
                    : props.warning
                      ? "bg-yellow-400"
                      : props.error
                        ? "bg-red-400"
                        : "bg-blue-500",
                )}
                style={{ width: `${100 - timerPercentage}%` }}
              />
            </div>
          )}
        </div>
      </Transition>
    </NotificationContainer>
  )
}

export default Notification
